import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import Sheet from '../components/Sheet';
import { useApp, useAppPath } from '../lib/AppContext';
import { rememberMode } from '../lib/mode';

const MODE_NAMES = { coach: 'Huấn luyện viên', personal: 'Cá nhân' };

function ModeCard({ active, dark, initial, title, text, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-current={active ? 'true' : undefined}
      className={`flex min-h-16 w-full items-center gap-3 rounded-[16px] border p-3 text-left ${active ? 'border-[#17804f] bg-[#e3f1e9] text-[#15261f]' : 'border-line bg-base-100'}`}>
      <span className={`grid size-10 shrink-0 place-items-center rounded-full font-bold ${dark ? 'bg-[#15261f] text-[#6fd3a0]' : 'bg-[#e3f1e9] text-[#17804f]'}`}>
        {initial}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-bold">{title}</span>
        {/* The active card is light green in both themes, so its text has fixed dark colours. */}
        <span className={`block text-xs ${active ? 'text-[#3d5a4c]' : 'text-muted'}`}>{text}</span>
      </span>
      {active ? <span className="text-[11px] font-bold text-[#17804f]">ĐANG DÙNG</span> : <Icon name="chevronRight" className="size-4 text-muted" />}
    </button>
  );
}

// A7 "Chuyển chế độ": personal app or coach mode, one tap, no signing out; and "Mở app ở chế độ đang dùng"
// to make the current mode where the app opens. From the personal app (the avatar) it also leads to
// Settings, which the avatar opened before.
export default function ModeSheet({ open, current, onClose }) {
  const { api, coach, setCoach, profile } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  if (!coach) return null;

  const go = (mode) => {
    onClose();
    if (mode === current) return;
    rememberMode(mode);
    navigate(path(mode === 'coach' ? '/coach' : '/'));
  };
  const opensHere = coach.launch_mode === current;
  const setLaunch = async (on) => {
    setSaving(true);
    try {
      const data = await api.patch('/coach', { launch_mode: on ? current : 'last' });
      setCoach((c) => ({ ...c, launch_mode: data.launch_mode }));
    } catch {
      // The toggle stays as it was; the setting is also in Tài khoản.
    } finally {
      setSaving(false);
    }
  };
  // The same number as the Yêu cầu tab's badge.
  const requests = coach.requests_count || 0;
  const studentsText = [`${coach.students_count ?? 0} học viên`, requests > 0 && `${requests} yêu cầu mới`].filter(Boolean).join(' · ');

  return (
    <Sheet open={open} onClose={onClose} title="Chuyển chế độ">
      <div className="flex flex-col gap-3">
        <ModeCard active={current === 'personal'} initial={<Avatar name={profile?.name} url={profile?.avatar_url} size={40} />}
          title="Cá nhân" text="Nhật ký, thực đơn và mục tiêu của bạn" onClick={() => go('personal')} />
        <ModeCard active={current === 'coach'} dark initial={(profile?.name || 'H').trim()[0]?.toUpperCase()}
          title="Huấn luyện viên" text={studentsText} onClick={() => go('coach')} />
        <label className="flex min-h-14 items-center justify-between gap-3 px-1">
          <span>
            <span className="block text-[15px] font-semibold">Mở app ở chế độ đang dùng</span>
            <span className="block text-xs text-muted">
              Khi mở app: {coach.launch_mode === 'last' ? 'chế độ dùng lần cuối' : MODE_NAMES[coach.launch_mode]}
            </span>
          </span>
          <input type="checkbox" className="toggle toggle-primary" checked={opensHere} disabled={saving} onChange={(e) => setLaunch(e.target.checked)} />
        </label>
        <p className="px-1 text-xs text-muted">Dữ liệu cá nhân và học viên tách riêng. Chuyển chế độ không đăng xuất.</p>
        {current === 'personal' && (
          <Link to={path('/settings')} onClick={onClose} className="flex min-h-12 items-center justify-between rounded-[14px] bg-base-200 px-4 text-sm font-semibold">
            <span className="flex items-center gap-2"><Icon name="cog" className="size-5" /> Cài đặt tài khoản</span>
            <Icon name="chevronRight" className="size-4 text-muted" />
          </Link>
        )}
      </div>
    </Sheet>
  );
}
